import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, addDoc, collection, updateDoc, serverTimestamp } from 'firebase/firestore';
import { LoanStatuses } from '../constants/statuses';

const NewApplication = () => {
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState('');
  const [yearsInBusiness, setYearsInBusiness] = useState('0');
  const [workingCapitalUse, setWorkingCapitalUse] = useState('');
  const [loanAmount, setLoanAmount] = useState('');
  const [numberOfEmployees, setNumberOfEmployees] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [creditScore, setCreditScore] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [lastYearRevenue, setLastYearRevenue] = useState('');
  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  
  // Additional admin fields
  const [notes, setNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [loanType, setLoanType] = useState('SBA');

  const loanTypes = [
    { value: 'SBA', label: 'SBA Loan' },
    { value: 'MCA', label: 'Merchant Cash Advance' },
    { value: 'AR_FINANCING', label: 'Accounts Receivable Financing' }
  ];

  const handleNumberInput = (e, setter) => {
    let value = e.target.value;
    
    if (!value && value !== '') {
      value = '';
    }
    
    value = value.toString().replace(/[^0-9.]/g, '');
    
    if (e.target.id === 'loanAmount') {
      value = value.replace(/[$,]/g, '');
    } else {
      value = value.replace(/\./g, '');
    }

    setter(value);
  };

  const sanitizeInput = (value, type) => {
    if (!value) return value;
    
    switch (type) {
      case 'number':
        const numericValue = value.toString().replace(/[^0-9]/g, '');
        return numericValue || '0';
      
      case 'email':
        return value.toString().trim().toLowerCase();
      
      case 'text':
        return value.toString().trim();
      
      default:
        return value;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLoading) return;
    setIsLoading(true);

    try {
      const now = serverTimestamp();
      
      const sanitizedData = {
        // Basic Information
        businessName: sanitizeInput(businessName, 'text'),
        yearsInBusiness: sanitizeInput(yearsInBusiness, 'number'),
        workingCapitalUse: sanitizeInput(workingCapitalUse, 'text'),
        loanSize: sanitizeInput(loanAmount, 'number'),
        numberOfEmployees: sanitizeInput(numberOfEmployees, 'number'),
        
        // Personal Information
        firstName: sanitizeInput(firstName, 'text'),
        lastName: sanitizeInput(lastName, 'text'),
        
        // Contact Information
        emails: [sanitizeInput(email, 'email')],
        phoneNumbers: [sanitizeInput(phoneNumber, 'number')],
        creditScores: [sanitizeInput(creditScore, 'number')],
        
        // Financial Information
        lastYearRevenue: sanitizeInput(lastYearRevenue, 'number'),
        
        // Personal Guarantors (single guarantor for admin form)
        personalGuarantors: [{
          firstName: sanitizeInput(firstName, 'text'),
          lastName: sanitizeInput(lastName, 'text')
        }],
        
        // Admin-specific fields
        notes: sanitizeInput(notes, 'text'),
        followUpDate: followUpDate,
        loanType: loanType,
        
        // Timestamps and Status
        createdAt: now,
        lastUpdated: now,
        lastUserUpdate: now,
        submissionTimestamp: now,
        
        // Default Values based on loan type
        loanPurpose: 'workingCapital',
        loanStatus: LoanStatuses.NEW_APPLICATION,
        
        loanStructure: {
          additionalNotes: notes,
          sba7aLoan: loanType === 'SBA' ? "100" : "0"
        },
        
        // Risk factors (default to none for admin-created applications)
        riskFactors: {
          hasBankruptcies: false,
          bankruptcyCount: 0,
          bankruptcyTimeframe: '',
          hasOutstandingTaxes: false,
          outstandingTaxAmount: 0,
          hasPendingLawsuits: false,
          hasNegativeIncome: false
        },
        
        // Mark as admin-created
        createdByAdmin: true,
        adminUserId: 'admin' // You might want to get actual admin user ID
      };

      console.log('Submitting admin application:', sanitizedData);

      const docRef = await addDoc(collection(db, 'loanApplications'), sanitizedData);

      await updateDoc(doc(db, 'loanApplications', docRef.id), {
        id: docRef.id
      });

      alert('Application created successfully!');
      navigate(`/loan-details/${docRef.id}`);
    } catch (error) {
      console.error("Submission error:", error);
      alert('Error: ' + (error.message || 'Please check all fields and try again.'));
    } finally {
      setIsLoading(false);
    }
  };

  const validateStep = () => {
    switch (step) {
      case 0:
        return businessName?.trim() && yearsInBusiness !== '' && lastYearRevenue;
      case 1:
        return workingCapitalUse?.trim() && loanAmount && numberOfEmployees;
      case 2:
        return firstName?.trim() && lastName?.trim() && email?.trim() && creditScore && phoneNumber?.trim();
      case 3:
        return loanType && (followUpDate || true); // Follow-up date is optional
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep()) {
      alert('Please fill out all required fields before proceeding.');
      return;
    }
    setStep(step + 1);
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const renderForm = () => {
    switch (step) {
      case 0:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Business Information</h3>
            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">Business Name *</label>
              <input
                type="text"
                id="businessName"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="yearsInBusiness" className="block text-sm font-medium text-gray-700">
                Years in Business *
              </label>
              <input
                type="number"
                id="yearsInBusiness"
                value={yearsInBusiness}
                onChange={(e) => handleNumberInput(e, setYearsInBusiness)}
                min="0"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="lastYearRevenue" className="block text-sm font-medium text-gray-700">
                Last Year Revenue *
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="text"
                  id="lastYearRevenue"
                  value={lastYearRevenue}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '');
                    setLastYearRevenue(value);
                  }}
                  placeholder="0"
                  required
                  className="block w-full pl-7 pr-12 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Loan Details</h3>
            <div>
              <label htmlFor="workingCapitalUse" className="block text-sm font-medium text-gray-700">Loan Purpose *</label>
              <textarea
                id="workingCapitalUse"
                value={workingCapitalUse}
                onChange={(e) => setWorkingCapitalUse(e.target.value)}
                required
                rows={3}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Equipment purchase, debt refinance, working capital, etc."
              />
            </div>
            <div>
              <label htmlFor="loanAmount" className="block text-sm font-medium text-gray-700">Loan Amount *</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="text"
                  id="loanAmount"
                  value={loanAmount}
                  onChange={(e) => handleNumberInput(e, setLoanAmount)}
                  placeholder="0"
                  required
                  className="block w-full pl-7 pr-12 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label htmlFor="numberOfEmployees" className="block text-sm font-medium text-gray-700">Number of Employees *</label>
              <input
                type="number"
                id="numberOfEmployees"
                value={numberOfEmployees}
                onChange={(e) => setNumberOfEmployees(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">First Name *</label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">Last Name *</label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email *</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">Phone Number *</label>
              <input
                type="tel"
                id="phoneNumber"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="creditScore" className="block text-sm font-medium text-gray-700">Credit Score *</label>
              <input
                type="number"
                id="creditScore"
                value={creditScore}
                onChange={(e) => setCreditScore(e.target.value)}
                min="300"
                max="850"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Admin Settings</h3>
            <div>
              <label htmlFor="loanType" className="block text-sm font-medium text-gray-700">Loan Type *</label>
              <select
                id="loanType"
                value={loanType}
                onChange={(e) => setLoanType(e.target.value)}
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                {loanTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="followUpDate" className="block text-sm font-medium text-gray-700">Follow-up Date</label>
              <input
                type="date"
                id="followUpDate"
                value={followUpDate}
                onChange={(e) => setFollowUpDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes</label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Add any notes about this application..."
              />
            </div>
            
            <div className="mt-8">
              <button
                type="submit"
                disabled={isLoading || !validateStep()}
                className="w-full inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                {isLoading ? 'Creating Application...' : 'Create Application'}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-200 bg-green-50">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Create New Application</h1>
                <p className="mt-1 text-sm text-gray-600">Admin portal for creating loan applications</p>
              </div>
              <button
                onClick={() => navigate('/admin-portal')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Back to Dashboard
              </button>
            </div>
          </div>

          <div className="px-8 py-6">
            {/* Progress indicator */}
            <div className="mb-8">
              <div className="flex items-center">
                {[0, 1, 2, 3].map((stepNumber) => (
                  <div key={stepNumber} className="flex items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        step >= stepNumber
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {stepNumber + 1}
                    </div>
                    {stepNumber < 3 && (
                      <div
                        className={`w-16 h-1 mx-2 ${
                          step > stepNumber ? 'bg-green-600' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2 flex justify-between text-xs text-gray-500">
                <span>Business Info</span>
                <span>Loan Details</span>
                <span>Contact Info</span>
                <span>Admin Settings</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {renderForm()}
              
              {step < 3 && (
                <div className="flex justify-between pt-6">
                  {step > 0 && (
                    <button
                      type="button"
                      onClick={handlePrevious}
                      className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Previous
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!validateStep()}
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 ml-auto"
                  >
                    Next
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewApplication; 